import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box, Typography, Stack, Chip, Alert, Button, TextField, IconButton, Tooltip,
  CircularProgress, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItemButton, InputAdornment, Divider,
} from '@mui/material';
import FolderRounded from '@mui/icons-material/FolderRounded';
import PictureAsPdfRounded from '@mui/icons-material/PictureAsPdfRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import ImageNotSupportedRounded from '@mui/icons-material/ImageNotSupportedRounded';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';

import {
  browseArtworkFolder, getArtworkThumbnailByPath, refreshArtworkSeparations,
  getArtworkPathSuggestions, ArtworkRefreshResult,
} from './productionApi';
import { productionKeys } from './queryKeys';
import { TOKENS } from './board/ganttConfig';

const BROWSE_ROOT = 'cloudflow://Production';
const PREVIEW_W = 260;

/** 'cloudflow://Production/A%26J%20AUST' -> ['Production', 'A&J AUST'] */
function segmentsOf(path: string): string[] {
  if (!path.startsWith('cloudflow://')) return [];
  return path.slice('cloudflow://'.length).split('/').filter(Boolean).map((s) => {
    try { return decodeURIComponent(s); } catch { return s; }
  });
}

function folderOf(path: string | null): string | null {
  if (!path || !path.startsWith('cloudflow://')) return null;
  const cut = path.lastIndexOf('/');
  const parent = cut > 'cloudflow://'.length ? path.slice(0, cut) : null;
  if (!parent) return null;
  return parent === BROWSE_ROOT || parent.startsWith(`${BROWSE_ROOT}/`) ? parent : null;
}

function apiErr(e: unknown): string {
  const r = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return r || (e as Error)?.message || 'Request failed';
}

/**
 * ArtworkPathDialog — point ONE finished-good at the right Cloudflow PDF and
 * re-read its separations.
 *
 * Two ways in, because both happen: BROWSE the Cloudflow store here (the common
 * case — you don't know the path, you know roughly where it lives), or paste a
 * path you already have from a Wave tab. Either way what's saved is an override
 * row against (customer_code, customer_ref) — migration 126 — so the correction
 * covers every job on that SKU, not just the one that exposed it.
 *
 * Saving always re-pulls: the separation, thumbnail and "no artwork" negative
 * caches are cleared server-side and the file is read fresh, so the result panel
 * shows the actual separations the new path yields.
 *
 * The read happens BEFORE the save, and a path that reads nothing is refused
 * (status 'rejected', item untouched). That matters because an override
 * suppresses every other candidate: a saved-but-wrong path pins the SKU to a
 * dead file with no way back except someone noticing and deleting the row.
 */
export default function ArtworkPathDialog({
  open, customerCode, customerRef, currentPath, overridden, onClose, onSaved,
}: {
  open: boolean;
  customerCode: string;
  customerRef: string;
  currentPath: string | null;
  overridden: boolean;
  onClose: () => void;
  /** Fired after a successful save/reset — the caller invalidates its own caches. */
  onSaved: (result: ArtworkRefreshResult) => void;
}) {
  // Start where the current artwork lives (its folder), else the client's own
  // folder — one click from the SKU folder in the overwhelmingly common case.
  const startFolder = folderOf(currentPath) ?? `${BROWSE_ROOT}/${encodeURIComponent(customerCode)}`;
  const [folder, setFolder] = useState(startFolder);
  const [selected, setSelected] = useState<string>(currentPath ?? '');
  const [filter, setFilter] = useState('');
  const [result, setResult] = useState<ArtworkRefreshResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-arm on each open: the dialog is mounted per row and reused across opens.
  useEffect(() => {
    if (!open) return;
    setFolder(startFolder);
    setSelected(currentPath ?? '');
    setFilter('');
    setResult(null);
    setError(null);
    // startFolder/currentPath are derived from the props this effect keys on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerCode, customerRef, currentPath]);

  const browseQ = useQuery({
    queryKey: productionKeys.artworkBrowse(folder),
    queryFn: () => browseArtworkFolder(folder),
    enabled: open,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const previewQ = useQuery({
    queryKey: productionKeys.artworkThumbnailByPath(selected, PREVIEW_W),
    queryFn: () => getArtworkThumbnailByPath(selected, PREVIEW_W),
    enabled: open && selected.startsWith('cloudflow://'),
    staleTime: Infinity,
    retry: false,
  });

  // Where else this SKU's artwork might be filed. Empty for almost everything —
  // it only answers for customers known to file under another code's tree (UBA
  // artwork living under MPA, both being Mars Petcare). No Cloudflow call, so
  // asking on every open is free, and the answer never changes for an item.
  const suggestQ = useQuery({
    queryKey: productionKeys.artworkPathSuggestions(customerCode, customerRef),
    queryFn: () => getArtworkPathSuggestions(customerCode, customerRef),
    enabled: open && !!customerCode && !!customerRef,
    staleTime: Infinity,
    retry: false,
  });
  // Nothing to suggest once the SKU already points there.
  const suggestions = (suggestQ.data ?? []).filter((s) => s.path !== (currentPath ?? ''));

  const save = useMutation({
    mutationFn: (payload: { path?: string; reset?: boolean }) => refreshArtworkSeparations({
      customer: customerCode, ref: customerRef, ...payload,
    }),
    // Adopt whatever the server actually resolved — after a reset that's the
    // convention path, not the override still sitting in the box. Without this
    // the field and the "Save" button disagree with what the SKU now uses, and
    // one more click would re-save the convention path AS an override.
    //
    // 'rejected' is the exception: the backend read the proposed path, found
    // nothing and saved NOTHING, so the SKU is unchanged. Keep the operator's
    // path in the box to edit, and don't fire onSaved — there is nothing for the
    // caller to invalidate, and refetching would wrongly imply something moved.
    onSuccess: (r) => {
      setResult(r);
      setError(null);
      if (r.status === 'rejected') return;
      if (r.path) setSelected(r.path);
      onSaved(r);
    },
    onError: (e: unknown) => { setError(apiErr(e)); setResult(null); },
  });

  const data = browseQ.data;
  const needle = filter.trim().toLowerCase();
  const folders = useMemo(
    () => (data?.folders ?? []).filter((f) => !needle || f.name.toLowerCase().includes(needle)),
    [data, needle],
  );
  const files = useMemo(
    () => (data?.files ?? []).filter((f) => !needle || f.name.toLowerCase().includes(needle)),
    [data, needle],
  );

  const crumbs = segmentsOf(folder);
  // What the SKU uses right now: the just-saved result if we've saved in this
  // dialog, else the path the caller opened us with (its data refetches behind us).
  // A REJECTED result changed nothing, so it must not be adopted as the live
  // path — otherwise the box and the Save button would both act as though the
  // path we just refused to save is the one in force.
  const applied = result && result.status !== 'rejected' ? result : null;
  const livePath = applied?.path ?? currentPath ?? '';
  const liveOverridden = applied ? applied.overridden : overridden;
  const canSave = selected.trim().startsWith('cloudflow://')
    && selected.trim() !== livePath && !save.isPending;

  // Breadcrumb click -> the cloudflow:// prefix of that depth, rebuilt from the
  // ENCODED path so we never hand Cloudflow a decoded segment.
  const crumbPath = (i: number) =>
    'cloudflow://' + folder.slice('cloudflow://'.length).split('/').filter(Boolean).slice(0, i + 1).join('/');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 0.5 }}>
        Artwork file — {customerCode} {customerRef}
        {liveOverridden && (
          <Chip size="small" label="✎ Modified"
            sx={{ ml: 1, fontSize: 10.5, fontWeight: 700, color: TOKENS.accent, bgcolor: `${TOKENS.accent}18` }} />
        )}
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.ink2, mb: 1 }}>
          Picks the PDF the separations are read from. Saved against the item
          ({customerCode} / {customerRef}), so it applies to every job on this SKU — and saving
          re-reads the file straight away.
        </Typography>

        {/* ---- Where else this artwork might be filed -----------------------
            A hint, never an action: clicking loads the folder and fills the box
            so you can look at the file. It is still only saved if you press Save
            AND the file reads. See _ARTWORK_CODE_ALIASES in blueprints/production.py
            for why this exists (UBA/MPA are one customer, filed under MPA). */}
        {!!suggestions.length && (
          <Alert
            severity="info"
            icon={<LightbulbOutlined sx={{ fontSize: 18 }} />}
            sx={{ mb: 1.25, py: 0.25, fontSize: 12 }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <span>{suggestions[0].reason}</span>
              {suggestions.map((s) => (
                <Tooltip key={s.path} title={s.path}>
                  <Chip
                    size="small" clickable label={`Try ${s.customer_ref}`}
                    onClick={() => {
                      setSelected(s.path);
                      const f = folderOf(s.path);
                      if (f) { setFolder(f); setFilter(''); }
                    }}
                    sx={{
                      height: 20, fontSize: 11, fontWeight: 700,
                      color: TOKENS.accent, bgcolor: `${TOKENS.accent}1a`,
                      border: `1px solid ${TOKENS.accent}55`,
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Alert>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          {/* ---- Cloudflow browser ------------------------------------------- */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <Tooltip title="Up one folder">
                <span>
                  <IconButton size="small" disabled={!data?.parent}
                    onClick={() => data?.parent && setFolder(data.parent)}>
                    <ArrowUpwardRounded fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.25 }}>
                {crumbs.map((seg, i) => (
                  <React.Fragment key={`${seg}-${i}`}>
                    {i > 0 && <span style={{ color: TOKENS.ink3, fontSize: 11 }}>/</span>}
                    <Box component="button" type="button"
                      onClick={() => setFolder(crumbPath(i))}
                      sx={{
                        border: 0, background: 'none', p: 0, cursor: 'pointer',
                        fontSize: 11.5, fontWeight: i === crumbs.length - 1 ? 700 : 500,
                        color: i === crumbs.length - 1 ? TOKENS.ink : TOKENS.ink2,
                        '&:hover': { color: TOKENS.accent },
                      }}>
                      {seg}
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
              <Tooltip title="Re-list this folder">
                <IconButton size="small" onClick={() => browseQ.refetch()}>
                  <RefreshRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <TextField
              size="small" fullWidth placeholder="Filter this folder…"
              value={filter} onChange={(e) => setFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchRounded sx={{ fontSize: 16 }} /></InputAdornment>
                ),
                sx: { fontSize: 12.5 },
              }}
            />

            <Box sx={{
              mt: 0.75, height: 300, overflowY: 'auto',
              border: '1px solid rgba(0,0,0,0.1)', borderRadius: '8px',
            }}>
              {browseQ.isFetching && <LinearProgress sx={{ height: 2 }} />}
              {data?.error && (
                <Alert severity="warning" sx={{ m: 1, fontSize: 12 }}>{data.error}</Alert>
              )}
              {browseQ.isError && (
                <Alert severity="error" sx={{ m: 1, fontSize: 12 }}>{apiErr(browseQ.error)}</Alert>
              )}
              <List dense disablePadding>
                {folders.map((f) => (
                  <ListItemButton key={f.path} onClick={() => { setFolder(f.path); setFilter(''); }}
                    sx={{ py: 0.35 }}>
                    <FolderRounded sx={{ fontSize: 16, color: '#c9a227', mr: 0.75 }} />
                    <Typography sx={{ fontSize: 12, minWidth: 0, wordBreak: 'break-all' }}>{f.name}</Typography>
                  </ListItemButton>
                ))}
                {files.map((f) => (
                  <ListItemButton key={f.path} selected={selected === f.path}
                    onClick={() => setSelected(f.path)} sx={{ py: 0.35 }}>
                    <PictureAsPdfRounded sx={{ fontSize: 16, color: '#b33', mr: 0.75 }} />
                    <Typography sx={{ fontSize: 12, minWidth: 0, wordBreak: 'break-all' }}>{f.name}</Typography>
                  </ListItemButton>
                ))}
                {!browseQ.isFetching && !folders.length && !files.length && (
                  <Typography sx={{ fontSize: 12, color: TOKENS.ink3, p: 1.5 }}>
                    {needle ? 'Nothing here matches that filter.' : 'No folders or PDFs here.'}
                  </Typography>
                )}
              </List>
            </Box>
            {!!data?.hidden_files && (
              <Typography sx={{ fontSize: 10.5, color: TOKENS.ink3, mt: 0.4 }}>
                {data.hidden_files} non-PDF file{data.hidden_files === 1 ? '' : 's'} in this folder not shown.
              </Typography>
            )}
          </Box>

          {/* ---- Preview of the selected file --------------------------------- */}
          <Box sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0 }}>
            <Typography sx={{ fontSize: 10, letterSpacing: 0.3, color: TOKENS.ink3, textTransform: 'uppercase', mb: 0.5 }}>
              Preview
            </Typography>
            <Box sx={{
              height: 190, borderRadius: '8px', bgcolor: TOKENS.lineSoft,
              border: '1px solid rgba(0,0,0,0.08)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            }}>
              {!selected ? (
                <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3, px: 2, textAlign: 'center' }}>
                  Pick a PDF to preview it.
                </Typography>
              ) : previewQ.isFetching ? <CircularProgress size={18} />
                : previewQ.data?.data_url ? (
                  <img src={previewQ.data.data_url} alt="artwork preview"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                ) : (
                  <Stack alignItems="center" spacing={0.5} sx={{ px: 2 }}>
                    <ImageNotSupportedRounded sx={{ fontSize: 22, color: 'rgba(0,0,0,0.25)' }} />
                    <Typography sx={{ fontSize: 10.5, color: TOKENS.ink3, textAlign: 'center' }}>
                      No preview — Cloudflow couldn’t render this file.
                    </Typography>
                  </Stack>
                )}
            </Box>

            {result && (
              <Alert
                severity={
                  result.status === 'extracted' ? 'success'
                    : result.status === 'rejected' ? 'error' : 'warning'
                }
                sx={{ mt: 1, py: 0.25, fontSize: 12 }}
              >
                {result.status === 'extracted' ? (
                  <>
                    Read {result.separations.length} separation
                    {result.separations.length === 1 ? '' : 's'}:{' '}
                    {result.separations.map((s) => s.name).join(', ')}
                  </>
                ) : result.status === 'rejected' ? (
                  <>
                    <strong>Not saved</strong> — nothing readable there, so this item is
                    unchanged. Try another file.
                    {result.error ? ` (${result.error})` : ''}
                  </>
                ) : (
                  <>Nothing readable at this path{result.error ? ` — ${result.error}` : '.'}</>
                )}
              </Alert>
            )}
          </Box>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <TextField
          fullWidth size="small" label="cloudflow:// path"
          value={selected} onChange={(e) => setSelected(e.target.value)}
          helperText="Browse above, or paste a path straight from Wave. Saved only if the file reads."
          InputProps={{ sx: { fontFamily: TOKENS.monoFont, fontSize: 12 } }}
          FormHelperTextProps={{ sx: { fontSize: 10.5 } }}
        />
        {error && <Alert severity="error" sx={{ mt: 1, fontSize: 12 }}>{error}</Alert>}
        {save.isPending && (
          <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3, mt: 0.75 }}>
            Reading the file from Cloudflow…
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Tooltip title={liveOverridden
          ? 'Drop the override and go back to the standard naming convention'
          : 'This item already uses the naming convention'}>
          <span style={{ marginRight: 'auto' }}>
            <Button disabled={!liveOverridden || save.isPending}
              onClick={() => save.mutate({ reset: true })} sx={{ color: TOKENS.ink2 }}>
              Reset to default
            </Button>
          </span>
        </Tooltip>
        <Button disabled={save.isPending}
          onClick={() => save.mutate({})}>
          Re-read current file
        </Button>
        <Button onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
        <Button variant="contained" disabled={!canSave}
          onClick={() => save.mutate({ path: selected.trim() })}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
          {save.isPending ? 'Saving…' : 'Save & re-pull'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
